import { useCallback } from 'react';
import { HttpStatusCode } from 'axios';
import {
  Flex,
  InputBox,
  PrimaryButton,
  StackScreen,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { ApiError, ApiErrorCode } from 'shared/src/api/httpClient';
import { AnalyticsScreen } from 'shared/src/context';
import { useHandleBackPressed, useInstanceId } from 'shared/src/hooks';
import {
  bridge,
  getAnonymousJWTTokenKey,
  isWeb,
  removeAblyCookie,
  sanitizeRedirectPath,
  setMemberToken,
  useNavigator,
} from 'shared/src/utils';
import { useRedirectIfLoggedIn } from '../../useRedirectIfLoggedIn';
import { useLogCompleteEmailLogin } from './hooks/useLogCompleteEmailLogin';
import { useLogViewSignIn } from './hooks/useLogViewSignIn';
import { useValidatedInput } from './hooks/useValidatedInput';
import { usePostEmailLogin } from './queries/usePostEmailLogin';
import { validateEmail, validateLoginPassword } from './validation';

const EmailLoginContent = () => {
  const { width } = useScreenSize();
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { navigator } = useNavigator();
  const instanceId = useInstanceId();
  const { mutateAsync: login, isPending } = usePostEmailLogin();
  const logCompleteEmailLogin = useLogCompleteEmailLogin();

  // 이미 로그인된 사용자는 로그인 화면에서 되돌려보낸다.
  useRedirectIfLoggedIn();

  useLogViewSignIn();

  const emailField = useValidatedInput(validateEmail);
  const passwordField = useValidatedInput(validateLoginPassword);
  const canSubmit = emailField.isValid && passwordField.isValid;

  const handlePressLogin = async () => {
    if (!canSubmit || isPending) {
      return;
    }
    emailField.setServerError(undefined);
    passwordField.setServerError(undefined);
    try {
      const { token } = await login({
        email: emailField.value,
        password: passwordField.value,
      });
      await setMemberToken(token);
      await logCompleteEmailLogin();

      if (isWeb) {
        const redirect = new URLSearchParams(window.location.search).get(
          'redirect',
        );
        // 외부 조작 가능한 값이므로 내부 경로만 허용(오픈 리다이렉트 방지).
        // replace로 이동해 히스토리에 로그인을 남기지 않는다(뒤로가기로 로그인에 다시
        // 안 돌아오게). webview router.replace · 소셜 로그인 · 이미-로그인 가드와 정합.
        window.location.replace(sanitizeRedirectPath(redirect));
        return;
      }
      bridge.closeInstance({ instanceId });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === HttpStatusCode.Unauthorized &&
        isWeb
      ) {
        // 401 실패 시 만료/오염된 anonymous 토큰 제거
        const anonymousKey = await getAnonymousJWTTokenKey();
        removeAblyCookie(anonymousKey);
      }
      if (error instanceof ApiError) {
        if (error.code === ApiErrorCode.EMAIL_VALIDATION) {
          emailField.setServerError(error.message);
          return;
        }
        if (error.code === ApiErrorCode.PASSWORD_VALIDATION) {
          passwordField.setServerError(error.message);
          return;
        }
      }
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '로그인에 실패했어요',
      );
    }
  };

  const handlePressFindId = useCallback(() => {
    navigator.moveToRecoveryId();
  }, [navigator]);

  const handlePressFindPassword = useCallback(() => {
    navigator.moveToRecoveryPassword();
  }, [navigator]);

  return (
    <StackScreen
      title={'로그인'}
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
    >
      <Flex flex={1} width={width} alignSelf={'center'}>
        <Flex padding={{ horizontal: 16, top: 16 }} gap={16}>
          <InputBox
            type={'underline'}
            placeholder={'이메일 입력'}
            value={emailField.value}
            onChangeText={emailField.onChangeText}
            keyboardType={'email-address'}
            autoCapitalize={'none'}
            textContentType={'username'}
            helperText={emailField.helperText}
          />
          <InputBox
            type={'password'}
            placeholder={'비밀번호 입력'}
            value={passwordField.value}
            onChangeText={passwordField.onChangeText}
            textContentType={'password'}
            helperText={passwordField.helperText}
          />
        </Flex>

        <Flex padding={{ horizontal: 16, top: 24 }}>
          <PrimaryButton
            variant={'fill'}
            color={'gray'}
            size={'xlarge'}
            state={isPending ? 'loading' : canSubmit ? undefined : 'disabled'}
            onPress={handlePressLogin}
          >
            {'로그인'}
          </PrimaryButton>
        </Flex>

        <Flex.Row
          justifyContent={'center'}
          alignItems={'center'}
          gap={16}
          padding={{ top: 16 }}
        >
          <Flex
            onPress={handlePressFindId}
            padding={{ vertical: 8, horizontal: 12 }}
          >
            <Typography type={'body2'} color={'content_secondary'}>
              {'아이디 찾기'}
            </Typography>
          </Flex>
          <Flex
            height={14}
            borderRightWidth={1}
            borderColor={'border_secondary'}
          />
          <Flex
            onPress={handlePressFindPassword}
            padding={{ vertical: 8, horizontal: 12 }}
          >
            <Typography type={'body2'} color={'content_secondary'}>
              {'비밀번호 찾기'}
            </Typography>
          </Flex>
        </Flex.Row>
      </Flex>
    </StackScreen>
  );
};

export const EmailLoginScreen = () => (
  <AnalyticsScreen screen={'SIGN_IN'} hostsModals={false}>
    <EmailLoginContent />
  </AnalyticsScreen>
);
